import { useState } from "react";
import { api, errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { PROVINCES } from "../../lib/constants";
import { formatLocation } from "../../lib/cities";
import { CityInput } from "../CityInput";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { MapPin, X, Loader2 } from "lucide-react";

const dismissKey = (id) => `cp_city_prompt_dismissed_${id}`;

export function CityPromptBanner() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(() => !!user && localStorage.getItem(dismissKey(user.user_id)) === "1");

  if (!user || user.location || hidden) return null;

  const dismiss = () => { localStorage.setItem(dismissKey(user.user_id), "1"); setHidden(true); };
  const save = async () => {
    if (!city.trim() || !province) return toast.error(t("cityp_need"));
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me/location", { location: formatLocation(city, province) });
      setUser(data);
      toast.success(t("cityp_saved"));
    } catch (err) { toast.error(errText(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4 sm:p-5" data-testid="city-prompt-banner">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm"><MapPin size={18} className="text-[#F5551E]" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-[#12234A]">{t("cityp_title")}</p>
          <p className="text-sm text-slate-600">{t("cityp_body")}</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <div className="sm:w-64">
              <CityInput value={city} onChange={setCity} province={province} testid="city-prompt-city" placeholder={t("cityp_city_ph")}
                onPick={(c) => { setCity(c.city); setProvince(c.province); }} className="h-10 rounded-xl bg-white" />
            </div>
            <Select value={province} onValueChange={setProvince}>
              <SelectTrigger data-testid="city-prompt-province" className="sm:w-52 h-10 rounded-xl bg-white"><SelectValue placeholder={t("cityp_prov_ph")} /></SelectTrigger>
              <SelectContent className="bg-white">
                {PROVINCES.filter((p) => p.code !== "all").map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={save} disabled={saving} data-testid="city-prompt-save" className="h-10 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">
              {saving && <Loader2 size={15} className="animate-spin" />} {t("cityp_save")}
            </Button>
          </div>
        </div>
        <button onClick={dismiss} aria-label={t("cityp_dismiss")} data-testid="city-prompt-dismiss" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>
    </div>
  );
}
